/**
 * @module engine3d/materials
 * @summary The material palette and the shading model used by the 3D view.
 * @description
 *   Materials are deliberately few and industrial: painted machine bodies,
 *   brushed and polished stainless, galvanised frame steel, rubber, cast iron,
 *   copper, plus a couple of translucent ones for sight glasses and water. Every
 *   3D model references these by key, so the whole plant reads as ONE drawing
 *   rather than ninety unrelated toys.
 *
 *   Shading is Blinn-Phong over a three-light rig with a hemispheric ambient
 *   term (sky above, warm floor bounce below). It is evaluated ONCE PER
 *   TRIANGLE — cheap enough for tens of thousands of triangles a frame, and on
 *   a 24+ segment barrel the per-triangle steps are far below the eye's
 *   threshold, so curved surfaces still read as smooth.
 *
 *   Edges are not a material property. The renderer draws the feature and
 *   silhouette lines each mesh authored (`Mesh.feat`), in a darker mix of the
 *   face's own shade; a material can only opt out (`lines: false`).
 *
 *   // INVARIANT: an albedo tuned to READ as a named colour (JCC navy, RAL
 *   //   7031) is tuned against the light rig and tone curve below — retune it
 *   //   whenever either of those changes, and check it on screen, not here.
 */
import { vNorm, v3, type Vec3 } from './math3';

export interface Material {
  /** Base colour, linear-ish 0..1 RGB. */
  albedo: [number, number, number];
  /** Specular intensity 0..1. */
  spec: number;
  /** Blinn-Phong exponent — higher is a tighter highlight. */
  shine: number;
  /** 0 = dielectric, 1 = metal (tints the highlight with the albedo). */
  metal: number;
  /** Surfaces with an inside the camera can see (troughs, open hoppers). */
  doubleSided?: boolean;
  /** < 1 for glass, water and safety screens. */
  alpha?: number;
  /**
   * `false` keeps this material out of the renderer's feature-line pass
   * altogether. Absent means lines are drawn wherever the geometry authored
   * them — on the still frame, on opaque faces (alpha >= 0.9).
   *
   * This replaces the old `crisp` flag, which asked for an outline round
   * EVERY triangle. That was never drawn, and could not have looked right: a
   * triangle edge is an artefact of tessellation, not an edge of the machine.
   * Machined edges now come from the edges each primitive marks as features
   * (`Mesh.feat`), so the choice left per material is only whether a surface
   * should carry lines at all — a granulate heap should not.
   */
  lines?: boolean;
}

export type MatKey = keyof typeof MATERIALS;

const rgb = (r: number, g: number, b: number): [number, number, number] => [
  r / 255,
  g / 255,
  b / 255,
];

/**
 * The palette. Colours are picked to sit beside the tool's JCC navy (#1e4d8c)
 * and orange (#f26522) without competing with the connector colours.
 *
 * An albedo here is NOT the colour you see: the rig below lights a vertical
 * face at roughly half strength and the tone curve compresses the top end.
 * Where a finish has to READ as a named colour (JCC navy, RAL 7031), the
 * albedo is the one that lands on that colour across the faces an isometric
 * view actually shows — lit top, key-lit front, shaded side — and the named
 * target is quoted beside it.
 */
export const MATERIALS = {
  /**
   * JCC navy — the default painted equipment skin. Reads as #1e4d8c on the lit
   * faces of an isometric view (top ≈ #2c68ac, key-lit front ≈ #1c406c).
   */
  painted: { albedo: rgb(40, 104, 200), spec: 0.35, shine: 42, metal: 0.1 },
  /** Lighter navy for secondary panels and covers. */
  paintedLight: { albedo: rgb(84, 134, 212), spec: 0.32, shine: 38, metal: 0.1 },
  /** Machine grey — frames, gearboxes, bases. */
  machine: { albedo: rgb(118, 124, 132), spec: 0.3, shine: 30, metal: 0.25 },
  machineDark: { albedo: rgb(72, 78, 86), spec: 0.3, shine: 34, metal: 0.3 },
  /** Polished stainless — vessels, hoppers, hygienic pipework. */
  stainless: { albedo: rgb(192, 201, 213), spec: 0.72, shine: 90, metal: 0.85 },
  /** Brushed / dull stainless for large silo walls (less mirror, less noise). */
  steel: { albedo: rgb(170, 176, 184), spec: 0.45, shine: 46, metal: 0.6 },
  /**
   * Galvanised structural steel — legs, frames, ladders, supports. A little
   * lighter and warmer than stainless, and matte where stainless is glossy,
   * so structure and product contact stop reading as the same grey.
   */
  galv: { albedo: rgb(206, 205, 198), spec: 0.2, shine: 16, metal: 0.3 },
  /** Carbon-steel process pipe. */
  pipe: { albedo: rgb(158, 166, 176), spec: 0.5, shine: 60, metal: 0.7 },
  /** Insulated / lagged line. */
  lagging: { albedo: rgb(214, 216, 214), spec: 0.14, shine: 12, metal: 0 },
  /** Cast-iron pump and gearbox housings. */
  cast: { albedo: rgb(92, 96, 102), spec: 0.24, shine: 22, metal: 0.35 },
  /**
   * Motor / drive housing — RAL 7031 blue-grey (approx.), so a drive stands
   * apart from the navy skin it is bolted to, from the dark barrel beside it
   * and from the galvanised frame under it.
   */
  motor: { albedo: rgb(90, 108, 122), spec: 0.36, shine: 40, metal: 0.3 },
  /** Heater bands and hot zones. */
  hot: { albedo: rgb(168, 74, 48), spec: 0.3, shine: 26, metal: 0.2 },
  /** Rubber belts, seals, tyres. */
  rubber: { albedo: rgb(46, 48, 52), spec: 0.14, shine: 16, metal: 0 },
  /** Safety yellow — handrails, guards, hazard marking. */
  safety: { albedo: rgb(226, 176, 40), spec: 0.28, shine: 30, metal: 0.1 },
  /** JCC orange accent — used sparingly for actuators and highlights. */
  accent: { albedo: rgb(224, 104, 44), spec: 0.34, shine: 36, metal: 0.1 },
  /** Copper conductor / heat-exchanger tube. */
  copper: { albedo: rgb(184, 108, 62), spec: 0.6, shine: 70, metal: 0.85 },
  /** Brass instrument bodies. */
  brass: { albedo: rgb(186, 156, 82), spec: 0.55, shine: 64, metal: 0.8 },
  /** White plastic / HDPE hoppers and covers. */
  plastic: { albedo: rgb(228, 230, 233), spec: 0.28, shine: 34, metal: 0 },
  /** Kraft fibreboard — octabins and cartons. */
  cardboard: { albedo: rgb(186, 152, 106), spec: 0.1, shine: 10, metal: 0 },
  /** Softwood pallets and dunnage. */
  timber: { albedo: rgb(170, 138, 96), spec: 0.12, shine: 12, metal: 0 },
  /** Concrete plinths and floor pads. */
  concrete: { albedo: rgb(176, 172, 164), spec: 0.08, shine: 8, metal: 0 },
  /** Control-panel fascia. */
  panel: { albedo: rgb(48, 54, 64), spec: 0.4, shine: 50, metal: 0.2 },
  /** Illuminated screen / indicator face. */
  screen: { albedo: rgb(96, 176, 208), spec: 0.5, shine: 60, metal: 0 },
  /** Sight glass, guards, inspection windows. */
  glass: { albedo: rgb(168, 202, 214), spec: 0.8, shine: 120, metal: 0.2, alpha: 0.34, doubleSided: true },
  /** Cooling water. */
  water: { albedo: rgb(86, 150, 186), spec: 0.6, shine: 90, metal: 0.1, alpha: 0.62, doubleSided: true },
  /** Polymer melt / compound in an open vessel. */
  melt: { albedo: rgb(198, 178, 140), spec: 0.24, shine: 22, metal: 0 },
  /** Pellets / granulate heap. */
  pellets: { albedo: rgb(206, 196, 176), spec: 0.18, shine: 18, metal: 0 },
  /** Cable / strand on a drum. */
  cable: { albedo: rgb(58, 60, 64), spec: 0.3, shine: 34, metal: 0.1 },
  /**
   * Open steel grating for access platforms. Genuinely see-through, which is
   * both what real plant decking looks like and what stops an upper floor from
   * hiding the machines underneath it.
   */
  grating: { albedo: rgb(148, 154, 162), spec: 0.3, shine: 26, metal: 0.5, alpha: 0.5, doubleSided: true },
  /** Deck edging and stair treads — solid, so the platform reads as a solid thing. */
  deck: { albedo: rgb(132, 138, 146), spec: 0.34, shine: 30, metal: 0.45 },
  /** Structural columns and beams carrying the decks. */
  column: { albedo: rgb(108, 114, 122), spec: 0.32, shine: 28, metal: 0.45 },

  // ── Premium kit v2 finishes. Each one exists to make a real part read as
  //    itself rather than as "grey": an electrical cabinet is RAL 7035 light
  //    grey, not a machine body; a slot is dark because no light reaches in. ──
  /** RAL 7035 light grey (approx.) — cabinets, consoles, junction boxes. */
  cabinet: { albedo: rgb(203, 208, 204), spec: 0.26, shine: 32, metal: 0.05 },
  /** HMI bezels, cabinet plinths, cable glands. */
  bezel: { albedo: rgb(34, 37, 42), spec: 0.32, shine: 44, metal: 0.1 },
  /** Dark glossy HMI glass — replaces the light-blue `screen` look on operator panels. */
  display: { albedo: rgb(34, 70, 100), spec: 0.85, shine: 140, metal: 0.05 },
  /**
   * Slots, grille openings, bores and recesses — a painted-in ambient occlusion.
   * No lines: ink mixed into near-black is near-black, so every stroke round a
   * grille slot would cost a path and show nothing. The dark fill against the
   * face it is cut into is already the crispest edge the slot can have.
   */
  cavity: { albedo: rgb(24, 26, 30), spec: 0.02, shine: 4, metal: 0, lines: false },
  /** Aluminium — heater shells, cylinder barrels, gauge heads, extrusion profiles. */
  alu: { albedo: rgb(178, 184, 190), spec: 0.45, shine: 40, metal: 0.65 },
  /** Hard chrome — godets, chill rolls, piston rods, polished rolls. */
  chrome: { albedo: rgb(222, 226, 232), spec: 0.95, shine: 160, metal: 0.95 },
  /** Emergency-stop red (ISO 13850 red on yellow) and the red stack-light tier. */
  signalRed: { albedo: rgb(196, 36, 32), spec: 0.35, shine: 40, metal: 0.05 },
  /** Green stack-light tier and "running" indicators. */
  signalGreen: { albedo: rgb(46, 160, 72), spec: 0.4, shine: 50, metal: 0 },

  // ── Manipulator handles. Flat and bright: these are UI, not plant, and they
  //    must stay legible against a shaded steel background from any angle. ──
  gizmoX: { albedo: rgb(226, 84, 78), spec: 0.1, shine: 12, metal: 0 },
  gizmoY: { albedo: rgb(104, 198, 118), spec: 0.1, shine: 12, metal: 0 },
  gizmoZ: { albedo: rgb(78, 150, 232), spec: 0.1, shine: 12, metal: 0 },
  gizmoHot: { albedo: rgb(255, 214, 64), spec: 0.12, shine: 14, metal: 0 },
  /** Pipe re-routing handle. */
  gizmoPipe: { albedo: rgb(246, 148, 62), spec: 0.12, shine: 14, metal: 0 },

  /**
   * Building fabric: a tower face or a room wall. Translucent enough to see the
   * pipework running against it, solid enough to read as a surface rather than
   * a tint.
   */
  wallPanel: { albedo: rgb(206, 210, 216), spec: 0.1, shine: 10, metal: 0, alpha: 0.3, doubleSided: true },
  /** Annotation surfaces (zone boxes) — flat, unlit-looking, translucent. */
  zone: { albedo: rgb(120, 152, 205), spec: 0.05, shine: 6, metal: 0, alpha: 0.16, doubleSided: true },
} satisfies Record<string, Material>;

// ─── Light rig ───────────────────────────────────────────────────────────

/** Direction TOWARD the key light (a high three-quarter sun from the left). */
export const KEY_DIR: Vec3 = vNorm(v3(-0.42, 0.82, 0.39));
const KEY_COL: [number, number, number] = [1.03, 1.0, 0.95];
const KEY_I = 0.86;

/** Cool fill from the opposite side, so shadowed faces never go dead black. */
const FILL_DIR: Vec3 = vNorm(v3(0.68, 0.24, -0.5));
const FILL_COL: [number, number, number] = [0.72, 0.8, 0.95];
const FILL_I = 0.3;

/** Rim light from behind to separate silhouettes from the floor. */
const RIM_DIR: Vec3 = vNorm(v3(0.15, 0.34, -0.93));
const RIM_I = 0.22;

/** Hemispheric ambient: cool sky above, warm bounce off the plant floor. */
const SKY: [number, number, number] = [0.42, 0.47, 0.56];
const GROUND: [number, number, number] = [0.3, 0.28, 0.25];

/**
 * Shade one triangle.
 *
 * @param n     Unit surface normal (world space), already flipped toward the eye
 *              for double-sided materials.
 * @param view  Unit vector from the surface TOWARD the camera.
 * @param m     The material.
 * @param tint  Optional selection/hover tint mixed into the albedo, 0..1.
 * @returns Packed 0xRRGGBB, ready for the renderer's colour-string cache.
 */
export function shade(
  n: Vec3,
  view: Vec3,
  m: Material,
  tintCol: [number, number, number] | null,
  tintAmt: number,
): number {
  let ar = m.albedo[0];
  let ag = m.albedo[1];
  let ab = m.albedo[2];
  if (tintCol && tintAmt > 0) {
    ar += (tintCol[0] - ar) * tintAmt;
    ag += (tintCol[1] - ag) * tintAmt;
    ab += (tintCol[2] - ab) * tintAmt;
  }

  // Hemispheric ambient.
  const hemi = n.y * 0.5 + 0.5;
  let r = ar * (GROUND[0] + (SKY[0] - GROUND[0]) * hemi);
  let g = ag * (GROUND[1] + (SKY[1] - GROUND[1]) * hemi);
  let b = ab * (GROUND[2] + (SKY[2] - GROUND[2]) * hemi);

  // Diffuse. "Half-Lambert" wrapping keeps the terminator soft, which is what
  // stops large cylindrical vessels reading as hard two-tone cutouts.
  const dK = Math.max(0, n.x * KEY_DIR.x + n.y * KEY_DIR.y + n.z * KEY_DIR.z);
  const dF = Math.max(0, (n.x * FILL_DIR.x + n.y * FILL_DIR.y + n.z * FILL_DIR.z) * 0.5 + 0.5);
  const kI = dK * KEY_I;
  const fI = dF * dF * FILL_I;
  r += ar * (KEY_COL[0] * kI + FILL_COL[0] * fI);
  g += ag * (KEY_COL[1] * kI + FILL_COL[1] * fI);
  b += ab * (KEY_COL[2] * kI + FILL_COL[2] * fI);

  // Rim — strongest where the surface turns away from the camera.
  const ndv = Math.max(0, n.x * view.x + n.y * view.y + n.z * view.z);
  const rim = Math.pow(1 - ndv, 3) * Math.max(0, n.x * RIM_DIR.x + n.y * RIM_DIR.y + n.z * RIM_DIR.z) * RIM_I;
  r += rim;
  g += rim * 1.02;
  b += rim * 1.1;

  // Blinn-Phong specular from the key light; metals tint it with their albedo.
  if (m.spec > 0) {
    const hx = KEY_DIR.x + view.x;
    const hy = KEY_DIR.y + view.y;
    const hz = KEY_DIR.z + view.z;
    const hl = Math.hypot(hx, hy, hz) || 1;
    const ndh = Math.max(0, (n.x * hx + n.y * hy + n.z * hz) / hl);
    const s = Math.pow(ndh, m.shine) * m.spec * (dK > 0 ? 1 : 0.15);
    r += s * (1 + (ar - 1) * m.metal);
    g += s * (1 + (ag - 1) * m.metal);
    b += s * (1 + (ab - 1) * m.metal);
  }

  // Filmic-ish shoulder, then quantise. Quantising to 6 bits per channel keeps
  // the renderer's colour-string cache to a few hundred live entries a frame
  // without any visible banding on these matte industrial surfaces.
  const R = tone(r);
  const G = tone(g);
  const B = tone(b);
  return (R << 16) | (G << 8) | B;
}

function tone(v: number): number {
  const x = v <= 0 ? 0 : v / (1 + v * 0.55);
  const q = Math.round(x * 63);
  const c = (q > 63 ? 63 : q) * 4;
  return c > 255 ? 255 : c;
}

// ─── Colour-string cache ─────────────────────────────────────────────────

const colorCache = new Map<number, string>();

/** `0xRRGGBB` → the `#rrggbb` string canvas wants, memoised. */
export function cssColor(packed: number): string {
  let s = colorCache.get(packed);
  if (s === undefined) {
    s = '#' + packed.toString(16).padStart(6, '0');
    // The cache is bounded by the 18-bit quantisation above, but clear it if a
    // pathological scene ever walks the whole space.
    if (colorCache.size > 40000) colorCache.clear();
    colorCache.set(packed, s);
  }
  return s;
}

/** Parse a `#rrggbb` document colour into a 0..1 triple for tinting. */
export function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '').trim();
  const n =
    h.length === 3
      ? parseInt(h[0] + h[0] + h[1] + h[1] + h[2] + h[2], 16)
      : parseInt(h.slice(0, 6), 16);
  if (!Number.isFinite(n)) return [0.6, 0.6, 0.6];
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}
